import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const useQueryParams = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // 단일 파라미터 읽기
  const getParam = useCallback(
    (key: string, defaultValue: string | number = '') => {
      const searchParams = new URLSearchParams(location.search);
      const paramValue = searchParams.get(key);

      if (paramValue === null) return defaultValue;

      if (typeof defaultValue === 'number') {
        const parsedNumber = Number(paramValue);
        return Number.isNaN(parsedNumber) ? defaultValue : parsedNumber;
      }

      return paramValue;
    },
    [location.search],
  );

  // 여러 파라미터 읽기
  const getAllParams = useCallback(
    (keys: string[], defaults: Record<string, string> = {}) => {
      const params = new URLSearchParams(location.search);
      const paramValues: Record<string, string> = {};

      keys.forEach((key) => {
        paramValues[key] = params.get(key) || defaults[key] || '';
      });

      return paramValues;
    },
    [location.search],
  );

  // 단일 파라미터 설정
  const setParam = useCallback(
    (
      key: string,
      value: string,
      options?: { resetPage?: boolean; replace?: boolean },
    ) => {
      const params = new URLSearchParams(location.search);
      params.set(key, value);

      if (options?.resetPage ?? true) {
        params.set('page', '1');
      }

      navigate(
        { search: params.toString() },
        { replace: options?.replace ?? false },
      );
    },
    [location.search, navigate],
  );

  const setAllParams = useCallback(
    (
      newParams: Record<string, string>,
      options?: { resetPage?: boolean; replace?: boolean },
    ) => {
      const searchParams = new URLSearchParams(location.search);

      Object.entries(newParams).forEach(([key, value]) => {
        searchParams.set(key, value);
      });

      if (options?.resetPage ?? true) {
        searchParams.set('page', '1');
      }

      navigate(
        { search: searchParams.toString() },
        { replace: options?.replace ?? false },
      );
    },
    [location.search, navigate],
  );

  return {
    getParam,
    getAllParams,
    setParam,
    setAllParams,
  };
};
